/* eslint-disable max-len */
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PrioritiesCard } from '../../../priority-list/PriorityList.constants';
import SymptomEditing from '../../../../member-detail/components/Symptoms/symptom-editing';
import DiagnosisEditing from '../../../../member-detail/components/Diagnoses/diagnoses-editing';
import SideEffectEditing from '../../../../member-detail/components/SideEffects/components/side-effect-editing';
import SubstanceUseEditing from '../../../../member-detail/components/SubstanceUse/substance-use-editing';
import { createNewAssociatedTag } from '../../../../../../services/member/member.service';
import { getProfile } from '../../../../../../redux/modules/profile/selectors';
import { getAuth } from '../../../../../../redux/modules/auth/selectors';
import { DomainTypesEnum } from '../../../../../../constants/CommonConstants';
import { useDomainType } from '../../../../../../hooks/useDomainType';
import AddNewReportMedications from '../../../../../provider-v2/member-detail/add-new-report/AddNewReportMedications';
import {
  fetchNavItemStatus,
  initiateMdToolboxAutomation,
} from '../../../../../../services/datalab/datalab.service';
import { showSnackbar } from '../../../../../../redux/modules/snackbar';
import { useDispatch } from 'react-redux';
import { getAssignedPrioritiesV2 } from '../../../../../../services/priority/priority.service';

const UpdatePriorityCard = ({ sectionType, card, patientId, onCloseCard, isNoDrawer, lookupData }) => {
  const medicalConditions = lookupData?.medicalCondition;
  const { meta } = useSelector(getAuth);
  const { profile } = useSelector(getProfile);
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [drawerAutomationCallback, setDrawerAutomationCallback] = useState(null);
  // Automation state for medication updates
  const [shouldStartAutomation, setShouldStartAutomation] = useState(false);
  const [automationSessionId, setAutomationSessionId] = useState(null);
  const [isAutomationLoading, setIsAutomationLoading] = useState(false);

  // Get domain options for medications if needed
  const [, , medicationOptions] = useDomainType({
    type: DomainTypesEnum.MEDICATIONS,
    patientId,
  });

  // Reset automation state when component unmounts
  useEffect(() => {
    return () => {
      setShouldStartAutomation(false);
      setAutomationSessionId(null);
      setIsAutomationLoading(false);
      setDrawerAutomationCallback(null);
    };
  }, []);

  const onUpdateSideEffect = async data => {
    setIsSaving(true);
    setError(null);
    try {
      await createNewAssociatedTag({
        patientId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: card.domainElementId,
            metaData: {
              notes: data.notes,
              interferenceInLife: data.isInterference,
              selectedImportance: data.status,
            },
          },
        ],
      });
      setIsSaving(false);
      onCloseCard(true);
    } catch (err) {
      setError(err.data.message || err.data.errors[0]?.endUserMessage);
      setIsSaving(false);
    }
  };

  const onUpdateSymptom = async data => {
    const { status: importanceLevel, notes, isInterference, ...remaining } = data;
    setIsSaving(true);
    setError(null);
    try {
      await createNewAssociatedTag({
        patientId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: card.domainElementId,
            metaData: {
              notes,
              interferenceInLife: isInterference,
              selectedImportance: importanceLevel,
              specification: {
                relatedToMedicalCondition: remaining.medicalConditions,
              },
            },
          },
        ],
      });
      setIsSaving(false);
      onCloseCard(true);
    } catch (err) {
      setError(err.data.message || err.data.errors[0]?.endUserMessage);
      setIsSaving(false);
    }
  };

  const onUpdateMedication = async (payload, handleQueued) => {
    setIsSaving(true);
    try {
      // Handle unified flow payload for editing
      const medicationData = payload.medications[0]; // Since editing only has one medication
      await createNewAssociatedTag({
        patientId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: card.domainElementId,
            metaData: {
              notes: medicationData?.additionalNotes,
              selectedImportance: medicationData?.status,
              rxDrugInfo: {
                sig: medicationData?.sig,
                dispenseAmount: medicationData?.dispenseAmount ? Number(medicationData.dispenseAmount) : 0,
                associatedDiagnoses: medicationData?.associatedDiagnoses,
                allowSubstitutions: medicationData?.allowSubstitutions,
                pharmacyNotes: medicationData?.pharmacyNotes,
                additionalNotes: medicationData?.additionalNotes,
                quantity: medicationData.quantity ? Number(medicationData.quantity) : 0,
                dose: medicationData.dose ?? '',
                frequency: medicationData?.frequency?.value ?? medicationData?.frequency,
                duration: medicationData?.duration ? Number(medicationData.duration) : 0,
                confidantProviderId: medicationData?.confidantProvider,
                confidantProviderName: medicationData?.confidantProviderName,
                prescribingProvider:
                  medicationData?.provider === 'Confidant Provider' ? 'Internal' : 'External',
                providerName: medicationData?.providerName,
                symptomsBeingTreated: medicationData?.symptoms,
                externalMedicationId: medicationData?.externalMedicationId,
                externalMedicationName: medicationData?.externalMedicationName,
                refills: medicationData?.refills ? Number(medicationData.refills) : 0,
                pharmacyId: payload.pharmacy?.id,
                pharmacyName: payload.pharmacy?.name,
                pharmacyCode: payload.pharmacy?.externalId,
                pharmacyZipCode: payload.pharmacy?.zipCode,
              },
            },
          },
        ],
      });

      const { data: updatedRecords } = await getAssignedPrioritiesV2({
        patientId,
        dataDomainType: 'medications',
        pageNumber: 0,
        pageSize: 20,
        searchQuery: '',
      });

      // Find the newly created association by matching domainElementId
      const targetDomainElementId = card.domainElementId;
      const newlyCreatedAssociation = updatedRecords.records.find(
        record => record.domainElementId === targetDomainElementId,
      );

      if (!newlyCreatedAssociation) {
        throw new Error('Could not find the newly created medication association');
      }

      // Set isSaving to false so UI can show automation updates
      setIsSaving(false);

      // Start MDToolbox automation after successful medication update
      if (medicationData?.confidantProvider) {
        const status = await fetchNavItemStatus({ providerId: medicationData.confidantProvider });
        const statusData = status?.data?.data;
        if (statusData && statusData['MDToolboxAccess']) {
          setIsAutomationLoading(true);
          const response = await initiateMdToolboxAutomation(
            {
              patientId,
              associatedTagId: newlyCreatedAssociation.id,
              providerId: medicationData.confidantProvider,
            },
            meta.userId,
          );

          if (!response.data?.success) {
            // Extract specific error message from backend response
            const errorMessage =
              response.data?.error?.message ||
              response.data?.message ||
              'Failed to submit the medication data. Please try again.';
            throw new Error(errorMessage);
          }

          // Extract sessionId from response for WebSocket communication
          const sessionId = response.data?.sessionId || response.sessionId;

          if (sessionId) {
            // Set automation state to trigger automation in the drawer
            setAutomationSessionId(sessionId);
            setShouldStartAutomation(true);
            setIsAutomationLoading(false);
            if (response.data?.isQueued && handleQueued) {
              handleQueued();
            }
          } else {
            console.warn('No sessionId received from ePrescribe automation');
            setIsSaving(false);
            onCloseCard(true);
          }
        } else {
          console.log('Skipping automation because of missing credentials...');
          setIsSaving(false);
          onCloseCard(true);
        }
      } else {
        // No confidantProvider id, skip automation and close normally
        console.log('Skipping automation...');
        setIsSaving(false);
        onCloseCard(true);
      }
    } catch (err) {
      const errorMessage =
        err.data?.error?.message || err.message || 'Failed to update medication. Please try again.';
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: errorMessage,
        }),
      );
      // Reset automation states but keep card open
      setIsSaving(false);
      setShouldStartAutomation(false);
      setIsAutomationLoading(false);
      setAutomationSessionId(null);
    }
  };

  // Reset automation state when closing the card
  const handleCloseCard = (shouldRefresh = false) => {
    // Reset automation state
    setShouldStartAutomation(false);
    setAutomationSessionId(null);
    setIsAutomationLoading(false);
    setDrawerAutomationCallback(null);

    // Call the original onCloseCard
    onCloseCard(shouldRefresh);
  };

  const onAutomationError = () => {
    // Reset loading states but keep drawer open
    setIsSaving(false);
    setIsAutomationLoading(false);
    setShouldStartAutomation(false);
    setAutomationSessionId(null);
  };

  const onUpdateSubstanceUse = async data => {
    setIsSaving(true);
    setError(null);
    try {
      await createNewAssociatedTag({
        patientId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: card.domainElementId,
            metaData: {
              interferenceInLife: data.isInterference,
              notes: data.notes,
              selectedImportance: data.status,
              substanceUse: {
                currentFrequencyOfUse: data.frequency,
                howLongUsingThisLevel: data.duration,
                lastUse: data.lastUse,
                methodOfUse: data.method,
                unitOfUse: data.dose.unit,
                dose: data.dose.amount,
                refillable: data.refillable,
              },
            },
          },
        ],
      });
      setIsSaving(false);
      onCloseCard(true);
    } catch (err) {
      setError(err.data.message || err.data.errors[0]?.endUserMessage);
      setIsSaving(false);
    }
  };

  const onUpdateDiagnosis = async data => {
    const { status: importanceLevel, notes, selectedIct10code } = data;
    setIsSaving(true);
    setError(null);
    try {
      await createNewAssociatedTag({
        patientId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: card.domainElementId,
            selectedIct10code,
            metaData: {
              notes,
              selectedImportance: importanceLevel,
            },
          },
        ],
      });
      setIsSaving(false);
      onCloseCard(true);
    } catch (err) {
      setError(err.data.message || err.data.errors[0]?.endUserMessage);
      setIsSaving(false);
    }
  };

  const getCard = () => {
    switch (sectionType) {
      case PrioritiesCard.SocialDeterminants:
      case PrioritiesCard.SideEffects:
      case PrioritiesCard.CognitiveIssues:
        return (
          <SideEffectEditing
            item={card}
            title={
              sectionType === PrioritiesCard.SocialDeterminants
                ? 'Update Social Determinant'
                : sectionType === PrioritiesCard.SideEffects
                ? 'Update Side Effect'
                : 'Update Cognitive Issue'
            }
            onClose={onCloseCard}
            isNoDrawer={isNoDrawer}
            onSubmit={onUpdateSideEffect}
            isOpen
            error={error}
            isSaving={isSaving}
          />
        );
      case PrioritiesCard.Symptoms:
        return (
          <SymptomEditing
            title="Update symptom"
            symptom={card}
            onClose={onCloseCard}
            isNoDrawer={isNoDrawer}
            onSave={onUpdateSymptom}
            isSaving={isSaving}
            error={error}
            type={DomainTypesEnum.SYMPTOMS}
            medicalConditions={medicalConditions}
            isOpen
          />
        );
      case PrioritiesCard.LifeEvents:
        return (
          <DiagnosisEditing
            item={card}
            title={sectionType === PrioritiesCard.LifeEvents ? 'Update life event' : 'Update diagnosis'}
            onClose={onCloseCard}
            isLifeEvent={sectionType === PrioritiesCard.LifeEvents}
            isNoDrawer={isNoDrawer}
            onSubmit={onUpdateDiagnosis}
            error={error}
            isSaving={isSaving}
            isOpen
            type={DomainTypesEnum.LIFE_EVENTS}
          />
        );
      case PrioritiesCard.Diagnoses:
        return (
          <DiagnosisEditing
            item={card}
            title={sectionType === PrioritiesCard.LifeEvents ? 'Update life event' : 'Update diagnosis'}
            onClose={onCloseCard}
            isLifeEvent={sectionType === PrioritiesCard.LifeEvents}
            isNoDrawer={isNoDrawer}
            onSubmit={onUpdateDiagnosis}
            error={error}
            isSaving={isSaving}
            isOpen
          />
        );
      case PrioritiesCard.Medications:
        return (
          <AddNewReportMedications
            isOpen={true}
            isNoDrawer={isNoDrawer}
            onClose={handleCloseCard}
            options={medicationOptions || []}
            onSubmit={onUpdateMedication}
            lookupData={lookupData}
            memberId={patientId}
            optionsLoading={false}
            isEditMode={true}
            existingMedication={card}
            isSaving={isSaving}
            shouldStartAutomation={shouldStartAutomation}
            setShouldStartAutomation={setShouldStartAutomation}
            isAutomationLoading={isAutomationLoading}
            automationSessionId={automationSessionId}
            onAutomationSuccess={() => {
              setAutomationSessionId(null);
              setShouldStartAutomation(false);
              setIsAutomationLoading(false);
              setIsSaving(false);
            }}
            onStartAutomation={callback => {
              setDrawerAutomationCallback(() => callback);
            }}
            onAutomationError={onAutomationError}
          />
        );
      case PrioritiesCard.SubstanceUse:
        return (
          <SubstanceUseEditing
            isNoDrawer={isNoDrawer}
            isOpen
            item={card}
            error={error}
            lookupData={lookupData}
            isSaving={isSaving}
            onClose={onCloseCard}
            onSubmit={onUpdateSubstanceUse}
          />
        );
      default:
        return null;
    }
  };

  return <>{getCard()}</>;
};

export { UpdatePriorityCard };
